// The design system is linked here and in this order on purpose: each sheet
// reads the ones above it, and every value in the page layer resolves against a
// token declared in the token sheet. The marker list is its own file as of
// 0.2.1 and does not arrive with the page shell.
// See js/design-system/phuketfit-reservations/.
import "../design-system/phuketfit-reservations/tokens/reservations-tokens.css";
import "../design-system/phuketfit-reservations/css/reservations-page-shell.css";
import "../design-system/phuketfit-reservations/css/reservations-marker-list.css";
import "../design-system/phuketfit-reservations/css/reservations-table.css";
import { FrontDesk } from "./FrontDesk";
import { PrintSheet } from "./PrintSheet";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Action, ApiError, Room, Schedule, request, shiftDate } from "./api";
import { SettingsView } from "./SettingsView";
import { Guide } from "./Guide";
import { History, Matrix, Workload } from "./Reports";
import "./housekeeping.css";
export function App({
  compact = false,
  onClose,
}: {
  compact?: boolean;
  onClose?: () => void;
}) {
  const [requestedFrom, setFrom] = useState(""),
    [days, setDays] = useState(7),
    [tab, setTab] = useState("Run Sheet"),
    [data, setData] = useState<Schedule | null>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [canManageSettings, setCanManageSettings] = useState(false);
  // The Guide reads only the rule set every schedule response already carries,
  // so it neither changes the fetch shape nor costs a request from any tab.
  const singleDay = compact || tab === "Run Sheet";
  const lastDate = useRef("");
  const from = requestedFrom || data?.from || lastDate.current;
  const requestVersion = useRef(0);
  const load = useCallback(
    async (preserveData = false) => {
      const version = ++requestVersion.current;
      setLoading(true);
      if (!preserveData) setData(null);
      setError("");
      try {
        const result = await request<Schedule>(
          `/schedule?days=${singleDay ? 1 : days}${requestedFrom ? `&from=${requestedFrom}` : ""}`,
        );
        if (version === requestVersion.current) {
          lastDate.current = result.from;
          setData(result);
          setCanManageSettings(result.capabilities.canManageSettings);
        }
      } catch (e) {
        if (version === requestVersion.current) {
          setError((e as Error).message);
          if (e instanceof ApiError && e.status === 403)
            setCanManageSettings(false);
        }
      } finally {
        if (version === requestVersion.current) setLoading(false);
      }
    },
    [requestedFrom, days, singleDay],
  );
  useEffect(() => {
    void load();
    const refresh = () => {
      void load();
      window.dispatchEvent(new Event("housekeeping-changed"));
    };
    window.addEventListener("housekeeping-settings-changed", refresh);
    return () => {
      requestVersion.current++;
      window.removeEventListener("housekeeping-settings-changed", refresh);
    };
  }, [load]);
  async function act(room: Room, body: Action) {
    try {
      await request(`/rooms/${room.roomId}/actions`, body);
      await load(true);
      window.dispatchEvent(new Event("housekeeping-changed"));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        await load(true);
        setError(
          `${e.message} The current room data has been reloaded. Review the task before recording again.`,
        );
      }
      throw e;
    }
  }
  const rangeView = tab === "Schedule" || tab === "Week Grid";
  return (
    // The page shell is for the report page only. The compact front desk is a
    // modal body and keeps its own chrome, so the page gutter and the 1180px
    // measure never leak into a dialog.
    <div
      className={`hk-app ${compact ? "hk-frontdesk" : "hk-report pfres-page pfres-page--panels"}`}
    >
      <Rail compact={compact}>
        <div className="hk-no-print">
          {compact ? (
            <header className="hk-heading">
              <h1 className="h5 mb-0">Housekeeping - Today's Run Sheet</h1>
              {onClose && (
                <button
                  className="btn-close"
                  aria-label="Close housekeeping"
                  onClick={onClose}
                />
              )}
            </header>
          ) : (
            <header className="pfres-page-header">
              <h1 className="pfres-page-header__title">Housekeeping</h1>
            </header>
          )}
          {!compact && (
            <nav aria-label="Housekeeping views">
              <ul className="pfres-tabs">
                {[
                  "Run Sheet",
                  "Schedule",
                  "Week Grid",
                  "History",
                  "Guide",
                  ...(canManageSettings ? ["Settings"] : []),
                ].map((t) => (
                  <li key={t}>
                    <button
                      className="pfres-tabs__link"
                      aria-current={tab === t ? "page" : undefined}
                      onClick={() => setTab(t)}
                    >
                      {t}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}
          {!compact && (tab === "Run Sheet" || rangeView) && (
            <div className="hk-datebar">
              <button
                className="btn btn-outline-contrast"
                disabled={!from}
                aria-label="Previous day or period"
                onClick={() => setFrom(shiftDate(from, rangeView ? -days : -1))}
              >
                ←
              </button>
              <label>
                Service date
                <input
                  type="date"
                  className="form-control"
                  value={from}
                  onChange={(e) => e.target.value && setFrom(e.target.value)}
                />
              </label>
              <button
                className="btn btn-outline-contrast"
                disabled={!from}
                aria-label="Next day or period"
                onClick={() => setFrom(shiftDate(from, rangeView ? days : 1))}
              >
                →
              </button>
              <button
                className="btn btn-outline-contrast"
                onClick={() => {
                  setFrom("");
                  if (!requestedFrom) void load();
                }}
              >
                Today
              </button>
              {rangeView && (
                <label>
                  Range
                  <select
                    className="form-select"
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                  </select>
                </label>
              )}
              {tab === "Run Sheet" && (
                <button
                  className="btn btn-outline-contrast ms-auto"
                  disabled={!data || loading}
                  onClick={() => window.print()}
                >
                  Print daily sheet
                </button>
              )}
            </div>
          )}
          {loading && !data && <p role="status">Loading housekeeping...</p>}
          {error && (
            <p className="alert alert-danger" role="alert">
              {error}
            </p>
          )}
          {data &&
            (compact ? (
              <FrontDesk data={data} act={act} />
            ) : tab === "Settings" && data.capabilities.canManageSettings ? (
              <SettingsView />
            ) : tab === "Guide" ? (
              <Guide data={data} />
            ) : tab === "History" ? (
              <History
                from={data.from}
                rooms={data.rooms}
                timezone={data.timezone}
              />
            ) : tab === "Schedule" ? (
              <Workload data={data} />
            ) : tab === "Week Grid" ? (
              <Matrix data={data} rooms={data.rooms} />
            ) : null)}
          {compact && (
            <footer className="hk-modal-footer">
              <a
                className="btn btn-outline-contrast"
                href="/housekeeping-report"
              >
                Run sheet and reports
              </a>
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </footer>
          )}
        </div>
        {data && !compact && (
          <PrintSheet data={data} preview={tab === "Run Sheet"} />
        )}
      </Rail>
    </div>
  );
}
// The measure rail every left edge on the page agrees with. Compact mode has no
// rail: the modal sizes the body.
function Rail({
  compact,
  children,
}: {
  compact: boolean;
  children: React.ReactNode;
}) {
  return compact ? (
    <>{children}</>
  ) : (
    <div className="pfres-page__inner">{children}</div>
  );
}
